/**
 * Map Voluum lander_LanderResource ↔ Standard tab form values.
 * Shapes follow docs/voluum-api.json (General lander).
 * For Media Devoted | Created By Ciro Autiero
 */

export interface VoluumLanderFormValues {
  namePostfix: string;
  url: string;
  landerType: 'LANDER' | 'PRELANDER';
  /** Voluum panel: “Lander Tracking Script” (API: track without redirects). */
  shouldHaveTrackingScript: boolean;
  /** Not in lander PUT schema; Voluum UI only, preserved if API returns it. */
  useGtmForVoluumScripts: boolean;
  numberOfOffers: number;
  preferredTrackingDomain: string;
  tagsCsv: string;
  countryCode: string;
  workspaceId: string;
  noteText: string;
  fullNameReadOnly: string;
}

const EMPTY: VoluumLanderFormValues = {
  namePostfix: '',
  url: '',
  landerType: 'LANDER',
  shouldHaveTrackingScript: false,
  useGtmForVoluumScripts: false,
  numberOfOffers: 1,
  preferredTrackingDomain: '',
  tagsCsv: '',
  countryCode: '',
  workspaceId: '',
  noteText: '',
  fullNameReadOnly: '',
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object';
}

export function parseVoluumLanderResource(raw: unknown): VoluumLanderFormValues {
  if (!isRecord(raw)) return { ...EMPTY };
  const country = isRecord(raw.country) ? raw.country : null;
  const ws = isRecord(raw.workspace) ? raw.workspace : null;
  const note = isRecord(raw.note) ? raw.note : null;
  const tags = Array.isArray(raw.tags)
    ? raw.tags.filter((t): t is string => typeof t === 'string')
    : [];
  const nOffers =
    typeof raw.numberOfOffers === 'number' && Number.isFinite(raw.numberOfOffers)
      ? Math.max(1, Math.round(raw.numberOfOffers))
      : 1;
  return {
    ...EMPTY,
    fullNameReadOnly: typeof raw.name === 'string' ? raw.name : '',
    namePostfix: typeof raw.namePostfix === 'string' ? raw.namePostfix : '',
    url: typeof raw.url === 'string' ? raw.url : '',
    landerType: raw.landerType === 'PRELANDER' ? 'PRELANDER' : 'LANDER',
    shouldHaveTrackingScript: Boolean(raw.shouldHaveTrackingScript),
    useGtmForVoluumScripts: (() => {
      if (typeof raw.useGtmForVoluumScripts === 'boolean') {
        return raw.useGtmForVoluumScripts;
      }
      if (typeof raw.useGtm === 'boolean') return raw.useGtm;
      return false;
    })(),
    numberOfOffers: nOffers,
    preferredTrackingDomain:
      typeof raw.preferredTrackingDomain === 'string'
        ? raw.preferredTrackingDomain
        : '',
    tagsCsv: tags.join(', '),
    countryCode: typeof country?.code === 'string' ? country.code : '',
    workspaceId: typeof ws?.id === 'string' ? ws.id : '',
    noteText: (() => {
      if (typeof note?.note === 'string') return note.note;
      if (typeof note?.text === 'string') return note.text;
      return '';
    })(),
  };
}

export function buildVoluumLanderUpdateBody(
  v: VoluumLanderFormValues,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    namePostfix: v.namePostfix.trim(),
    url: v.url.trim(),
    shouldHaveTrackingScript: v.shouldHaveTrackingScript,
    landerType: v.landerType,
    numberOfOffers: v.numberOfOffers,
  };
  const domain = v.preferredTrackingDomain.trim();
  if (domain) body.preferredTrackingDomain = domain;
  const tagList = v.tagsCsv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (tagList.length) body.tags = tagList;
  const cc = v.countryCode.trim().toUpperCase();
  if (cc) body.country = { code: cc };
  const wid = v.workspaceId.trim();
  if (wid) body.workspace = { id: wid };
  const note = v.noteText.trim();
  if (note) body.note = { note };
  return body;
}
